import {
  ArrowCircleRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';

import { FALLBACK_LIMITS, useConfig } from '@entities/config/api';
import {
  isResultOpen,
  SEARCH_RUN_STAGES,
  stageKey,
  useSearchResults,
  useSearchRun,
} from '@entities/search-run';
import { criteriaChips, useSearchSession } from '@entities/search-session';
import { VehicleCard } from '@entities/vehicle/VehicleCard';
import { KarlDialogue } from '@features/chat';
import { ShowcaseCard } from '@features/search';
import { orderStageView } from '@features/searches';
import { queryKeys } from '@shared/api/queryKeys';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import {
  ButtonLink,
  Container,
  KarlIntro,
  NextStepAction,
  ProgressRing,
  Skeleton,
  StateBlock,
} from '@shared/ui';

import styles from './AnalysisPage.module.css';

/** Сколько примеров показывается в карусели: витрина целиком — на шаге 2. */
const SAMPLE_SIZE = 4;

/**
 * Экран анализа (FE-221, референс `screen3`, MVP_PRD §12, §34.1).
 *
 * Экран показывает **настоящую** работу прогона: реальные этапы worker'а,
 * реальный процент и реальные числа каталога.
 *
 * Три запрета, из-за которых экран выглядит иначе, чем референс:
 *
 * * **числа только из API.** `screen3` показывает «1.542.867 Angebote» и
 *   «1,5 Mio. Angebote» — таких чисел в CarViQ.DE быть не может: система работает
 *   с внутренним каталогом и не ищет в интернете (CLAUDE.md §2). Компонент
 *   статистики не принимает литералов;
 * * **панель сверху — не фильтры, а подтверждённые критерии.** Менять их
 *   «на месте» нельзя: правка критериев создаёт новую версию и новый прогон,
 *   и делается она в диалоге с Karl (§35.4);
 * * **строка географии рисуется только при реальных данных.** Нет ступеней —
 *   нет строки: «в вашей земле 0» было бы выдуманным фактом (§43.3).
 */
export function AnalysisPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const { searchId = '' } = useParams();

  const { data: config } = useConfig();
  const limits = config?.limits ?? FALLBACK_LIMITS;

  const sessionQuery = useSearchSession(searchId || null);
  const session = sessionQuery.data ?? null;
  const runId = session?.session.current_search_run_id ?? null;

  const runQuery = useSearchRun(runId);
  const run = runQuery.data ?? null;
  const readable = run !== null && (run.status === 'completed' || run.status === 'stale');

  const samplesQuery = useSearchResults(runId, { page: 1, pageSize: SAMPLE_SIZE }, readable);
  // Витрина до оплаты приходит открытыми карточками (BE-580), после — полными:
  // режим задаёт сервер, а не экран (MVP_TECH_RFC §34.11).
  const samples = (samplesQuery.data?.items ?? []).slice(0, SAMPLE_SIZE);

  const percent = Math.max(0, Math.min(100, run?.progress.percent ?? 0));
  const currentIndex =
    run?.progress.stage === null || run?.progress.stage === undefined
      ? -1
      : SEARCH_RUN_STAGES.indexOf(run.progress.stage);

  const chips =
    session === null
      ? []
      : criteriaChips(session.criteria.payload, (key, params) => t(key, params ?? {}), locale);

  /**
   * Стадия заказа меняется в момент завершения прогона — на сервере, внутри
   * той же транзакции. Состояние заказа само по себе не опрашивается (§22),
   * поэтому без этой инвалидации экран остался бы на анализе до перезагрузки,
   * хотя четвёрка уже собирается. Один запрос на переход, а не таймер.
   */
  const runStatus = run?.status ?? null;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (runStatus === null || runStatus === 'queued' || runStatus === 'processing') return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
  }, [queryClient, runStatus, searchId]);

  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: {
      progressPercent: percent,
      progressLabel: t('analysis.progress.label'),
      // Диалог живёт в рельсе на всех экранах после шага 1: уточнить запрос
      // можно и во время анализа — уточнение пересоберёт подбор (§35.4).
      extra: <KarlDialogue searchId={searchId} />,
    },
    context: (
      <div className={styles.rail}>
        <section className={styles.railCard} aria-labelledby="analysis-progress-title">
          <h2 id="analysis-progress-title" className={styles.railTitle}>
            {readable ? t('analysis.progress.titleDone') : t('analysis.progress.title')}
          </h2>
          {/* Кольцо прогресса — единственное разрешённое исключение из запрета
              круглых спиннеров: процент настоящий и приходит с сервера (§5.9). */}
          <ProgressRing
            percent={percent}
            size={132}
            label={t('analysis.progress.done')}
            title={t('analysis.progress.label')}
          />
        </section>

        <section className={styles.railCard} aria-labelledby="analysis-checklist-title">
          <h2 id="analysis-checklist-title" className={styles.railTitle}>
            {t('analysis.checklist.title')}
          </h2>
          <ol className={styles.checklist}>
            {SEARCH_RUN_STAGES.map((stage, index) => {
              const done = readable || currentIndex > index;
              // Текущий этап — синий: синий показывает то, что происходит
              // сейчас (DESIGN_SYSTEM §2.3). Галочка остаётся только у
              // сделанного, и состояние называется словом, а не одним цветом.
              const active = !done && currentIndex === index && run?.status === 'processing';
              const state = done ? 'done' : active ? 'active' : 'pending';
              return (
                <li
                  key={stage}
                  className={
                    done ? styles.checkDone : active ? styles.checkActive : styles.checkPending
                  }
                  aria-current={active ? 'step' : undefined}
                >
                  {active ? (
                    <ArrowCircleRightIcon size={16} weight="fill" aria-hidden="true" />
                  ) : (
                    <CheckCircleIcon
                      size={16}
                      weight={done ? 'fill' : 'regular'}
                      aria-hidden="true"
                    />
                  )}
                  {t(stageKey(stage))}
                  <span className="visually-hidden">
                    {` — ${t(`results.progress.state.${state}`)}`}
                  </span>
                </li>
              );
            })}
          </ol>
          {/* Оговорка о границах системы — рядом со списком, а не в подвале. */}
          <p className={styles.railNote}>{t('results.disclaimer')}</p>
        </section>
      </div>
    ),
    contextLabel: t('analysis.railLabel'),
  });

  // --- Перестройка закончилась ----------------------------------------------

  // Оплаченный заказ бывает на этом экране только во время перестройки подбора
  // (MVP_PRD §35.4): уточнение критериев возвращает его к анализу. Как только
  // воронка снова дошла до четвёрки, показывать здесь нечего — экран анализа
  // не его шаг, и заказ уходит туда, где новая четвёрка. Стадии `analyzing`,
  // `analysis_failed` и `stale` ведут на этот же экран, поэтому ожидание и
  // разбор сорвавшегося прогона редирект не задевает.
  if (session !== null && session.session.paid) {
    const screen = orderStageView(session.session.stage).screen;
    if (screen === 'top4') return <Navigate to={ROUTES.searchTop4(searchId)} replace />;
  }

  if (sessionQuery.isPending || (runId !== null && runQuery.isPending)) {
    return (
      <Container>
        <div className={styles.page} aria-hidden="true">
          <Skeleton height="2.5rem" width="50%" />
          <Skeleton height="8rem" radius="var(--radius-card)" />
          <Skeleton height="12rem" radius="var(--radius-card)" />
        </div>
      </Container>
    );
  }

  if (runId === null) {
    return (
      <Container>
        <div className={styles.page}>
          <StateBlock
            title={t('results.noRun.title')}
            description={t('results.noRun.text')}
            action={
              <ButtonLink
                to={ROUTES.searchAssistant(searchId)}
                iconEnd={<ArrowRightIcon size={16} weight="bold" />}
              >
                {t('results.noRun.action')}
              </ButtonLink>
            }
          />
        </div>
      </Container>
    );
  }

  const stats = [
    { code: 'catalog', value: run?.total_catalog_count ?? null },
    { code: 'eligible', value: run?.eligible_count ?? null },
    { code: 'results', value: run?.result_count ?? null },
    { code: 'top', value: limits.top_selection_size },
  ];

  const geoCounts = run?.geo_step_counts ?? null;

  return (
    <Container>
      <div className={styles.page}>
        <Link to={ROUTES.app} className={styles.back}>
          <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
          {t('searches.backToList')}
        </Link>

        <header className={styles.header}>
          <h1 className={styles.title}>
            {t('analysis.title.before')}{' '}
            <span className={styles.accent}>{t('analysis.title.accent')}</span>{' '}
            {t('analysis.title.after')}
          </h1>
          {/* Вводный абзац — реплика Karl (DESIGN_SYSTEM §7.2): текст
              фиксированный, из локалей, LLM его не пишет. */}
          <KarlIntro>{t('analysis.lead')}</KarlIntro>
        </header>

        <section className={styles.criteria} aria-labelledby="analysis-criteria-title">
          <div className={styles.sectionHead}>
            <h2 id="analysis-criteria-title" className={styles.sectionTitle}>
              {t('analysis.criteria.title')}
            </h2>
            {/* Ссылки на отдельный экран диалога больше нет: диалог с Karl
                стоит рядом, в рельсе (FE-193). Поэтому подсказка набрана
                текстом, а не синим: синий на экране — это ссылка (§2.3). */}
            <p className={styles.hint}>
              <PencilSimpleIcon size={14} aria-hidden="true" />
              {t('analysis.criteria.edit')}
            </p>
          </div>

          {chips.length === 0 ? (
            <p className={styles.muted}>{t('analysis.criteria.empty')}</p>
          ) : (
            <ul className={styles.chips}>
              {chips.map((chip) => (
                <li
                  key={chip.key}
                  className={[styles.chip, chip.strength === 'required' && styles.chipRequired]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {chip.label}
                  {/* Жёсткость названа словом: заливка и цвет её не сообщают. */}
                  <span className="visually-hidden">
                    {' '}
                    — {t(`criteria.strength.${chip.strength}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className={styles.muted}>{t('analysis.criteria.note')}</p>
        </section>

        <section className={styles.stats} aria-labelledby="analysis-stats-title">
          <h2 id="analysis-stats-title" className={styles.sectionTitle}>
            {t('analysis.stats.title')}
          </h2>
          <dl className={styles.statsGrid}>
            {stats.map((item) => (
              <div key={item.code} className={styles.stat}>
                <dt>{t(`analysis.stats.${item.code}`)}</dt>
                {/* «Нет данных» — текст, а не число: крупным числовым
                    кеглем он читался бы как значение (DESIGN_SYSTEM §8). */}
                {item.value === null ? (
                  <dd className={styles.statEmpty}>{t('catalog.noData')}</dd>
                ) : (
                  <dd className="tabular">{new Intl.NumberFormat(locale).format(item.value)}</dd>
                )}
              </div>
            ))}
          </dl>

          {/* Строка географии появляется только при реальных ступенях набора. */}
          {geoCounts === null ? null : (
            <p className={styles.geo}>
              {t('analysis.geography', {
                home: geoCounts.home ?? 0,
                neighbours: geoCounts.neighbours ?? 0,
              })}
            </p>
          )}

          <p className={styles.muted}>
            {t('analysis.stats.note', { limit: limits.shortlist_max })}
          </p>
        </section>

        {samples.length > 0 ? (
          <section className={styles.samples} aria-labelledby="analysis-samples-title">
            <div className={styles.sectionHead}>
              <h2 id="analysis-samples-title" className={styles.sectionTitle}>
                {t('analysis.samples.title')}
              </h2>
              <Link to={ROUTES.searchResults(searchId)} className={styles.link}>
                {t('analysis.samples.all')}
                <ArrowRightIcon size={14} aria-hidden="true" />
              </Link>
            </div>
            {/* Примеры — те же открытые карточки, что на витрине (FE-586):
                цена и данные объявления открыты, аналитики нет, порядок
                случайный и рейтингом не является (MVP_PRD §34.2). */}
            <p className={styles.muted}>{t('showcase.orderIsRandom')}</p>
            <ul className={styles.sampleGrid}>
              {samples.map((item, index) => (
                <li key={item.vehicle_id}>
                  {isResultOpen(item) ? (
                    <ShowcaseCard
                      searchId={searchId}
                      runId={runId}
                      item={item}
                      index={index}
                      to={`${ROUTES.searchVehicle(searchId, item.vehicle_id)}?run=${encodeURIComponent(runId)}`}
                    />
                  ) : (
                    <VehicleCard vehicle={item} index={index} />
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Переход к рабочему набору — главное действие экрана; на телефоне
            его несёт полоса оболочки рядом с Karl (FE-585). */}
        {readable ? (
          <NextStepAction
            to={ROUTES.searchResults(searchId)}
            className={styles.cta}
            iconEnd={<ArrowRightIcon size={16} weight="bold" />}
          >
            {t('analysis.openResults')}
          </NextStepAction>
        ) : null}
      </div>
    </Container>
  );
}
